import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Library, ChevronRight, Network, ScrollText, Lightbulb, FolderTree } from "lucide-react";
import ForceGraph2D from "react-force-graph-2d";
import { useApi } from "../../../lib/api";
import { resolveIcon } from "./iconMap";

// MJOS-143 / MJOS-182 — the Unified Brain.
// One store, four lenses (tabs): Knowledge Base (a desktop folder grid derived
// from slug paths), Session Logs (raw episodic capture), Learnings (distilled
// self-memory), and Index (a live force-directed graph of the cross-link web).
// KB + nav: /api/kb-nav · memory lenses: /api/memories · graph: /api/kb-graph.

type ApiPage = {
  page_slug: string;
  nav_label: string | null;
  nav_icon: string | null;
  nav_order: number | null;
  created_at: string;
};
type ApiFolder = { slug_path: string; label: string; icon: string | null; sort_order: number };

type TreeNode = {
  key: string;
  label: string;
  icon: string | null;
  order: number;
  // ISO timestamp — for files: the page's own created_at; for folders: MAX
  // created_at across all descendants (so a folder ranks by its freshest page).
  createdAt: string;
  to?: string;
  children: TreeNode[];
};

type MemoryType = "session_log" | "learning" | "user_fact" | "area" | "principle" | "identity";
type MemoryRow = {
  id: string;
  agent: string | null;
  type: MemoryType;
  title: string | null;
  body: string;
  metadata: Record<string, unknown>;
  created_at: string;
};

type GNode = { id: string; label: string; group: string; kind: "page" | "folder" };
type GLink = { source: string; target: string; kind: "tree" | "xref" };

type TabKey = "kb" | "logs" | "learnings" | "index";

function titleCase(seg: string): string {
  return seg
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function buildTree(pages: ApiPage[], folders: ApiFolder[]): TreeNode[] {
  const folderMeta = new Map(folders.map((f) => [f.slug_path, f]));
  const root: TreeNode = { key: "", label: "", icon: null, order: 0, createdAt: "", children: [] };
  const byPath = new Map<string, TreeNode>();

  function ensureFolder(path: string): TreeNode {
    const existing = byPath.get(path);
    if (existing) return existing;
    const segs = path.split("/");
    const parentPath = segs.slice(0, -1).join("/");
    const parent = parentPath ? ensureFolder(parentPath) : root;
    const meta = folderMeta.get(path);
    const node: TreeNode = {
      key: path,
      label: meta?.label ?? titleCase(segs[segs.length - 1]),
      icon: meta?.icon ?? "Folder",
      order: meta?.sort_order ?? 999,
      createdAt: "",
      children: [],
    };
    parent.children.push(node);
    byPath.set(path, node);
    return node;
  }

  for (const p of pages) {
    const stripped = p.page_slug.replace(/^(kb-doc|pitch-doc)\//, "");
    const segs = stripped.split("/");
    const folderPath = segs.slice(0, -1).join("/");
    const parent = folderPath ? ensureFolder(folderPath) : root;
    parent.children.push({
      key: p.page_slug,
      label: p.nav_label ?? titleCase(segs[segs.length - 1]),
      icon: p.nav_icon,
      order: p.nav_order ?? 999,
      createdAt: p.created_at,
      to: "/" + p.page_slug,
      children: [],
    });
  }

  // Roll the MAX createdAt up from leaves so folder rows reflect their newest page.
  const rollUp = (n: TreeNode): string => {
    if (n.children.length === 0) return n.createdAt;
    let max = n.createdAt;
    for (const c of n.children) {
      const child = rollUp(c);
      if (child > max) max = child;
    }
    n.createdAt = max;
    return max;
  };
  for (const c of root.children) rollUp(c);

  // Sort newest-first by createdAt; ties fall back to label.
  const sortRec = (n: TreeNode) => {
    n.children.sort((a, b) => {
      if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? 1 : -1;
      return a.label.localeCompare(b.label);
    });
    n.children.forEach(sortRec);
  };
  sortRec(root);
  return root.children;
}

const isFolder = (n: TreeNode) => n.children.length > 0;
function countLeaves(n: TreeNode): number {
  if (!isFolder(n)) return 1;
  return n.children.reduce((s, c) => s + countLeaves(c), 0);
}

const TYPE_STYLES: Record<MemoryType, string> = {
  session_log: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  learning: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  user_fact: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  area: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  principle: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
  identity: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
};
const AGENT_COLORS: Record<string, string> = {
  jarvis: "text-blue-500",
  atlas: "text-orange-500",
  ben: "text-emerald-500",
  nova: "text-violet-500",
  emma: "text-pink-500",
  iris: "text-sky-500",
};
function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatCreatedAt(iso: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function FolderRow({ folder, onOpen }: { folder: TreeNode; onOpen: () => void }) {
  const Icon = resolveIcon(folder.icon);
  const n = countLeaves(folder);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full items-center gap-3 border-b border-sky-200/60 bg-sky-50/60 px-4 py-3 text-left transition-colors first:rounded-t-2xl last:rounded-b-2xl last:border-b-0 hover:bg-sky-100 dark:border-sky-900/30 dark:bg-sky-950/20 dark:hover:bg-sky-900/40"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 transition-colors group-hover:bg-sky-200/70 dark:bg-sky-900/40 dark:group-hover:bg-sky-900/60">
        <Icon className="h-5 w-5 text-sky-600 dark:text-sky-300" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold leading-tight text-foreground">{folder.label}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {n} {n === 1 ? "page" : "pages"}
        </div>
      </div>
      <div className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatCreatedAt(folder.createdAt)}</div>
    </button>
  );
}

function FileRow({ page }: { page: TreeNode }) {
  const Icon = resolveIcon(page.icon);
  return (
    <Link
      to={page.to!}
      className="group flex w-full items-center gap-3 border-b bg-card px-4 py-3 transition-colors first:rounded-t-2xl last:rounded-b-2xl last:border-b-0 hover:bg-muted/60"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted/70 transition-colors group-hover:bg-muted">
        <Icon className="h-5 w-5 text-stone-500 dark:text-stone-400" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium leading-tight text-foreground">{page.label}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">page</div>
      </div>
      <div className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatCreatedAt(page.createdAt)}</div>
    </Link>
  );
}

function MemoryList({ rows, emptyHint }: { rows: MemoryRow[] | null; emptyHint: string }) {
  if (rows === null) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (rows.length === 0)
    return (
      <div className="rounded-xl border bg-muted/30 p-8 text-center">
        <p className="text-sm font-medium">Nothing here yet</p>
        <p className="mt-1 text-xs text-muted-foreground">{emptyHint}</p>
      </div>
    );
  return (
    <div className="space-y-3">
      {rows.map((m) => (
        <div key={m.id} className="rounded-xl border bg-card p-4 transition-colors hover:border-foreground/20">
          <div className="mb-2 flex items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${TYPE_STYLES[m.type]}`}
              >
                {m.type.replace("_", " ")}
              </span>
              {m.agent && (
                <span className={`text-xs font-semibold ${AGENT_COLORS[m.agent] ?? "text-muted-foreground"}`}>
                  {m.agent}
                </span>
              )}
              {m.title && <span className="text-sm font-medium">{m.title}</span>}
            </div>
            <span className="shrink-0 text-[11px] text-muted-foreground">{formatTimestamp(m.created_at)}</span>
          </div>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/90">{m.body}</pre>
        </div>
      ))}
    </div>
  );
}

function GraphView() {
  const api = useApi();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<{ nodes: GNode[]; links: GLink[] } | null>(null);
  const [stats, setStats] = useState<{ pages: number; folders: number; xrefs: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dims, setDims] = useState({ w: 800, h: 560 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api("/api/kb-graph");
        if (!res.ok) throw new Error(`/api/kb-graph ${res.status}`);
        const d = (await res.json()) as {
          nodes: GNode[];
          links: GLink[];
          stats?: { pages: number; folders: number; xrefs: number };
        };
        if (alive) {
          setData({ nodes: d.nodes ?? [], links: d.links ?? [] });
          setStats(d.stats ?? null);
        }
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setDims({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div>
      {stats && (
        <p className="mb-2 text-xs text-muted-foreground">
          {stats.pages} pages · {stats.folders} folders · {stats.xrefs} cross-links — click a node to open it.
        </p>
      )}
      <div ref={wrapRef} className="h-[70vh] w-full overflow-hidden rounded-xl border bg-card">
        {err && <p className="p-4 text-sm text-red-600">Failed to load graph: {err}</p>}
        {!err && !data && <p className="p-4 text-sm text-muted-foreground">Loading graph…</p>}
        {!err && data && (
          <ForceGraph2D
            width={dims.w}
            height={dims.h}
            graphData={data}
            backgroundColor="#ffffff"
            nodeLabel={(n: GNode) => n.label}
            nodeRelSize={5}
            nodeVal={(n: GNode) => (n.kind === "folder" ? 6 : 2)}
            nodeColor={(n: GNode) => (n.kind === "folder" ? "#0ea5e9" : "#94a3b8")}
            linkColor={(l: GLink) => (l.kind === "xref" ? "rgba(200,90,62,0.55)" : "rgba(148,163,184,0.22)")}
            linkWidth={(l: GLink) => (l.kind === "xref" ? 1.5 : 0.5)}
            linkDirectionalParticles={(l: GLink) => (l.kind === "xref" ? 3 : 0)}
            linkDirectionalParticleWidth={2}
            linkDirectionalParticleColor={() => "#c85a3e"}
            cooldownTicks={140}
            onNodeClick={(n: GNode) => {
              if (n.kind === "page") navigate("/" + n.id);
            }}
          />
        )}
      </div>
    </div>
  );
}

const TABS: { key: TabKey; label: string; icon: typeof Network }[] = [
  { key: "kb", label: "Knowledge Base", icon: FolderTree },
  { key: "logs", label: "Session Logs", icon: ScrollText },
  { key: "learnings", label: "Learnings", icon: Lightbulb },
  { key: "index", label: "Index", icon: Network },
];

export function KnowledgeBaseListPage() {
  const api = useApi();
  const [tab, setTab] = useState<TabKey>("kb");
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trail, setTrail] = useState<TreeNode[]>([]);
  const [memories, setMemories] = useState<MemoryRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api("/api/kb-nav");
        if (!res.ok) throw new Error(`/api/kb-nav ${res.status}`);
        const data = (await res.json()) as { pages: ApiPage[]; folders: ApiFolder[] };
        if (alive) setTree(buildTree(data.pages ?? [], data.folders ?? []));
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    (async () => {
      try {
        const res = await api("/api/memories");
        if (res.ok && alive) setMemories((await res.json()) as MemoryRow[]);
      } catch {
        /* memory lenses degrade gracefully */
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = trail.length ? trail[trail.length - 1] : null;
  const items = current ? current.children : tree;
  const folders = items.filter(isFolder);
  const files = items.filter((n) => !isFolder(n));
  const total = tree.reduce((n, f) => n + countLeaves(f), 0);

  const logs = useMemo(() => memories?.filter((m) => m.type === "session_log") ?? null, [memories]);
  const learnings = useMemo(
    () => memories?.filter((m) => m.type === "learning" || m.type === "principle") ?? null,
    [memories],
  );

  return (
    <div className="max-w-6xl px-6 pb-12 pt-4">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500/15">
          <Library className="h-5 w-5 text-sky-600 dark:text-sky-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">The Brain</h1>
          <p className="text-sm text-muted-foreground">
            One store, four lenses — knowledge, logs, learnings, and the live index.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-5 flex flex-wrap items-center gap-1 border-b">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                active
                  ? "border-sky-500 text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "kb" && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-1 text-sm">
            <button
              type="button"
              onClick={() => setTrail([])}
              className={`transition-colors hover:text-foreground ${
                trail.length ? "text-muted-foreground" : "font-medium text-foreground"
              }`}
            >
              {total} pages · {tree.length} folders
            </button>
            {trail.map((node, i) => (
              <span key={node.key} className="flex items-center gap-1">
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                <button
                  type="button"
                  onClick={() => setTrail(trail.slice(0, i + 1))}
                  className={`transition-colors hover:text-foreground ${
                    i === trail.length - 1 ? "font-medium text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {node.label}
                </button>
              </span>
            ))}
          </div>
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {error && <p className="text-sm text-red-600">Failed to load: {error}</p>}
          {!loading && !error && (
            <div className="space-y-4">
              {folders.length > 0 && (
                <div>
                  <div className="mb-1.5 flex items-center justify-between px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <span>Folders</span>
                    <span>Date created</span>
                  </div>
                  <div className="overflow-hidden rounded-2xl border border-sky-200/70 dark:border-sky-900/40">
                    {folders.map((f) => (
                      <FolderRow key={f.key} folder={f} onOpen={() => setTrail([...trail, f])} />
                    ))}
                  </div>
                </div>
              )}
              {files.length > 0 && (
                <div>
                  <div className="mb-1.5 flex items-center justify-between px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <span>Pages</span>
                    <span>Date created</span>
                  </div>
                  <div className="overflow-hidden rounded-2xl border">
                    {files.map((p) => (
                      <FileRow key={p.key} page={p} />
                    ))}
                  </div>
                </div>
              )}
              {folders.length === 0 && files.length === 0 && (
                <p className="text-sm text-muted-foreground">This folder is empty.</p>
              )}
            </div>
          )}
        </>
      )}

      {tab === "logs" && (
        <MemoryList rows={logs} emptyHint="Session logs are captured every few turns and surface here." />
      )}
      {tab === "learnings" && (
        <MemoryList rows={learnings} emptyHint="Distilled learnings and principles surface here." />
      )}
      {tab === "index" && <GraphView />}
    </div>
  );
}

(KnowledgeBaseListPage as unknown as { path: string }).path = "/knowledge-base";
